import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api, API } from "@/lib/api";
import { Search, Filter, Download, FileText, Tag, ShieldCheck, Receipt as ReceiptIcon, Eye } from "lucide-react";
import { PrintScheduleCSection, PrintPreviewDrawer } from "@/components/PrintScheduleC";
import { ComplianceStatuteModal, STATUTE_TEXT } from "@/components/ComplianceStatuteModal";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const CAT_LABELS = {
  all: "All Categories",
  meals: "Meals & Entertainment",
  office_supplies: "Office Supplies",
  electronics: "Electronics",
  transportation: "Transportation",
  groceries: "Groceries",
  software: "Software",
  travel: "Travel",
  entertainment: "Entertainment",
  other: "Other",
};

export default function Receipts() {
  const [receipts, setReceipts] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [yearFilter, setYearFilter] = useState("all");
  const [statuteToken, setStatuteToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const AVAILABLE_YEARS = ["2024", "2025", "2026"];

  // CPA Email Handoff
  const [sendToCpa, setSendToCpa] = useState(false);
  const [cpaEmail, setCpaEmail] = useState("");
  const [cpaSending, setCpaSending] = useState(false);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [scheduleCLoading, setScheduleCLoading] = useState(false);

  const load = () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (category && category !== "all") params.category = category;
    api
      .get("/receipts", { params })
      .then((r) => setReceipts(r.data || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [category]);

  useEffect(() => {
    const id = setTimeout(load, 250);
    return () => clearTimeout(id);
    // eslint-disable-next-line
  }, [q]);

  const exportPDF = () => {
    try {
      const doc = new jsPDF({ unit: "pt", format: "letter" });
      doc.setFillColor(15, 23, 42);
      doc.rect(0, 0, doc.internal.pageSize.getWidth(), 60, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(18);
      doc.text("Receipt Sheets — Archive Export", 40, 38);
      doc.setTextColor(16, 185, 129);
      doc.setFontSize(9);
      doc.text(new Date().toLocaleString(), 40, 52);
      const rows = receipts.map((r) => [
        r.date, r.merchant, (CAT_LABELS[r.category] || r.category),
        `$${Number(r.subtotal || 0).toFixed(2)}`,
        `$${Number(r.tax || 0).toFixed(2)}`,
        `$${Number(r.total || 0).toFixed(2)}`,
        r.tax_deductible ? "YES" : "",
        (r.tags || []).join(", "),
      ]);
      autoTable(doc, {
        startY: 80,
        head: [["Date", "Merchant", "Category", "Subtotal", "Tax", "Total", "Ded.", "Tags"]],
        body: rows,
        theme: "grid",
        headStyles: { fillColor: [16, 185, 129], textColor: 15, fontStyle: "bold" },
        bodyStyles: { fontSize: 9, textColor: 40 },
        alternateRowStyles: { fillColor: [245, 250, 248] },
        columnStyles: {
          3: { halign: "right" }, 4: { halign: "right" }, 5: { halign: "right", fontStyle: "bold" },
        },
      });
      const total = receipts.reduce((s, r) => s + Number(r.total || 0), 0);
      const deductible = receipts.reduce((s, r) => s + (r.tax_deductible ? Number(r.total || 0) : 0), 0);
      const y = doc.lastAutoTable.finalY + 20;
      doc.setFontSize(10);
      doc.setTextColor(15, 23, 42);
      doc.text(`Receipts: ${receipts.length}   |   Total: $${total.toFixed(2)}   |   Tax Deductible: $${deductible.toFixed(2)}`, 40, y);
      doc.save(`receipts-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success("PDF exported");
    } catch (e) {
      toast.error("PDF export failed");
    }
  };

  // ---------- Audit-Safe PDF Bundle ------------------------------------------
  // Builds an IRS-audit-ready packet: cover page + one page per Audit-Safe
  // receipt including the fetched image (when available) and the full statute
  // text pulled from STATUTE_TEXT keyed off the compliance token's code.
  // Cap 50 receipts to keep the browser responsive.
  const AUDIT_BUNDLE_CAP = 50;

  const fetchImageAsDataUrl = async (imagePath) => {
    if (!imagePath) return null;
    try {
      const res = await fetch(`${API}/files/${imagePath}`, { credentials: "include" });
      if (!res.ok) return null;
      const blob = await res.blob();
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  };

  const exportAuditPacket = async () => {
    const auditSafe = receipts.filter(
      (r) => r?.compliance_verification_token?.badge === "audit-safe",
    );
    if (auditSafe.length === 0) {
      toast.error("No Audit-Safe receipts to bundle yet.");
      return;
    }
    const truncated = auditSafe.length > AUDIT_BUNDLE_CAP;
    const bundle = auditSafe.slice(0, AUDIT_BUNDLE_CAP);
    const toastId = toast.loading(`Bundling ${bundle.length} Audit-Safe receipts…`);
    try {
      const doc = new jsPDF({ unit: "pt", format: "letter" });
      const W = doc.internal.pageSize.getWidth();
      const H = doc.internal.pageSize.getHeight();
      const M = 48;
      const now = new Date();
      // COVER
      doc.setFillColor(15, 23, 42);
      doc.rect(0, 0, W, 90, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(22);
      doc.text("IRS Audit Packet · Receipt Sheets", M, 46);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(103, 232, 249);
      doc.text(`Compiled ${now.toLocaleString()}`, M, 66);
      doc.setTextColor(148, 163, 184);
      doc.text(`${bundle.length} of ${auditSafe.length} Audit-Safe receipts bundled${truncated ? " · truncated for size" : ""}`, M, 80);

      // Statutory summary on cover
      let y = 130;
      doc.setTextColor(0);
      doc.setFont("helvetica", "bold"); doc.setFontSize(12);
      doc.text("Bundle Summary", M, y); y += 6;
      doc.setDrawColor(200); doc.line(M, y, W - M, y); y += 18;
      doc.setFont("helvetica", "normal"); doc.setFontSize(11);
      const total = bundle.reduce((s, r) => s + Number(r.total || 0), 0);
      const byCode = bundle.reduce((acc, r) => {
        const c = r.compliance_verification_token?.code || "—";
        acc[c] = (acc[c] || 0) + 1;
        return acc;
      }, {});
      doc.text(`Total deductible amount: $${total.toFixed(2)}`, M, y); y += 16;
      doc.text(`Statutes cited:`, M, y); y += 14;
      Object.entries(byCode).forEach(([code, n]) => {
        doc.text(`   · ${code} — ${n} receipt${n === 1 ? "" : "s"}`, M, y); y += 14;
      });

      // Cover footer disclaimer
      doc.setFont("helvetica", "italic"); doc.setFontSize(9); doc.setTextColor(110);
      doc.text(
        "This packet compiles substantiation for expenses classified as Audit-Safe under IRC §162 / §179 / §170 and related sub-sections.",
        M, H - 60,
      );
      doc.text(
        "Not legal or tax advice. Confirm every deduction with a qualified CPA.",
        M, H - 46,
      );

      // Per-receipt pages
      for (let i = 0; i < bundle.length; i++) {
        const r = bundle[i];
        const tok = r.compliance_verification_token || {};
        const statute = STATUTE_TEXT[tok.code] || null;
        doc.addPage();
        let py = 60;
        doc.setTextColor(15, 23, 42);
        doc.setFont("helvetica", "bold"); doc.setFontSize(14);
        doc.text(`${i + 1}. ${r.merchant || "—"}`, M, py); py += 6;
        doc.setDrawColor(200); doc.line(M, py, W - M, py); py += 18;
        doc.setFont("helvetica", "normal"); doc.setFontSize(11);
        doc.text(`Date: ${r.date || "—"}`, M, py);
        doc.text(`Total: $${Number(r.total || 0).toFixed(2)}`, W - M, py, { align: "right" }); py += 16;
        doc.text(`Category: ${CAT_LABELS[r.category] || r.category || "—"}`, M, py);
        doc.text(`Statute: ${tok.code || "—"}`, W - M, py, { align: "right" }); py += 22;

        // Attempt to embed the receipt image
        if (r.image_path) {
          try {
            toast.loading(`Bundling ${i + 1}/${bundle.length}… fetching image`, { id: toastId });
            const dataUrl = await fetchImageAsDataUrl(r.image_path);
            if (dataUrl) {
              const maxW = W - M * 2;
              const maxH = 260;
              // jsPDF picks format from data URL prefix. Fall back to JPEG on error.
              try {
                doc.addImage(dataUrl, "JPEG", M, py, maxW, maxH, undefined, "FAST");
              } catch {
                try { doc.addImage(dataUrl, "PNG", M, py, maxW, maxH, undefined, "FAST"); } catch { /* skip */ }
              }
              py += maxH + 18;
            }
          } catch { /* skip on any image failure */ }
        }

        // Statute text
        doc.setFont("helvetica", "bold"); doc.setFontSize(11);
        doc.text(statute?.heading || tok.title || "IRS Compliance Statute", M, py); py += 14;
        doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(40);
        const body = statute?.body || "Category not currently mapped to a specific IRS statute in our compliance registry.";
        const lines = doc.splitTextToSize(body, W - M * 2);
        // Force new page if body would overflow
        if (py + lines.length * 13 > H - 60) {
          doc.addPage();
          py = 60;
        }
        doc.text(lines, M, py); py += lines.length * 13 + 8;
        doc.setFont("helvetica", "italic"); doc.setFontSize(9); doc.setTextColor(110);
        doc.text(`Reg refs: ${statute?.reg_ref || "—"} · Deductible portion: ${tok.deductible_pct ?? 100}%`, M, py);
        doc.setTextColor(0);
      }

      const fname = `audit_safe_bundle_${now.toISOString().slice(0,10)}.pdf`;
      doc.save(fname);
      toast.success(`Audit packet exported · ${bundle.length} receipts`, {
        id: toastId,
        description: truncated ? `Capped at ${AUDIT_BUNDLE_CAP} — filter by category to bundle the rest.` : fname,
      });
    } catch (err) {
      toast.error("Audit packet export failed", { id: toastId });
    }
  };

  const exportCSV = async () => {
    try {
      const resp = await fetch(`${API}/export/csv`, { credentials: "include" });
      if (!resp.ok) throw new Error("Export failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `receipts-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const filteredReceipts = yearFilter === "all"
    ? receipts
    : receipts.filter((r) => String(r.date || "").startsWith(yearFilter));

  const exportScheduleC = async () => {
    if (scheduleCLoading) return;
    setScheduleCLoading(true);
    try {
      const year = yearFilter === "all" ? undefined : yearFilter;
      // SPEC 802: Server-side ReportLab binary PDF — direct blob download.
      const yearParam = year ? `?year=${year}` : "";
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/tax-docs/download-schedule-c${yearParam}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("session_token") || ""}`,
          },
        }
      );
      if (!resp.ok) {
        const errJson = await resp.json().catch(() => ({}));
        throw new Error(errJson.detail || `Server returned ${resp.status}`);
      }
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `receiptsheets-schedule-c-pre-fill-${year || new Date().getFullYear() - 1}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
      toast.success(`Schedule C PDF downloaded · Tax Year ${year || new Date().getFullYear() - 1}`);

      // CPA Email Handoff — still uses jsPDF base64 for email attachment if enabled.
      if (sendToCpa) {
        const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(cpaEmail);
        if (!emailOk) {
          toast.error("Enter a valid CPA email to send the handoff");
          return;
        }
        try {
          setCpaSending(true);
          // Fetch JSON summary for the CPA email body
          const { data } = await api.get("/export/schedule-c", { params: year ? { year } : {} });
          const jsPDFModule = await import("jspdf");
          const autoTableMod = await import("jspdf-autotable");
          const doc = new jsPDFModule.default({ orientation: "portrait", unit: "pt" });
          doc.setFillColor(15, 23, 42); doc.rect(0, 0, 612, 60, "F");
          doc.setTextColor(16, 185, 129); doc.setFontSize(11);
          doc.text("RECEIPT SHEETS · IRS SCHEDULE C EXPORT", 40, 28);
          doc.setTextColor(255, 255, 255); doc.setFontSize(20);
          doc.text(`Tax Year ${data.year || "All"}`, 40, 50);
          let y = 90;
          doc.setTextColor(100, 116, 139); doc.setFontSize(10);
          doc.text(`Grand deductible: $${data.grand_total_deductible.toFixed(2)}   |   ${data.receipt_count} receipts   |   ${data.line_count} Schedule C lines`, 40, y);
          y += 20;
          data.lines.forEach((block) => {
            autoTableMod.default(doc, {
              startY: y,
              head: [[`Line ${block.line} · ${block.label}`, "Merchant", "Date", "Deductible"]],
              body: block.receipts.map((r) => [r.id?.slice(-6) || "-", r.merchant?.slice(0, 32) || "", r.date || "", `$${r.amount_deductible.toFixed(2)}`]),
              foot: [["", "", "Subtotal", `$${block.total.toFixed(2)}`]],
              theme: "grid",
              headStyles: { fillColor: [16, 185, 129], textColor: [15, 23, 42] },
              footStyles: { fillColor: [30, 41, 59], textColor: [16, 185, 129], fontStyle: "bold" },
              styles: { fontSize: 9, cellPadding: 5 },
            });
            y = doc.lastAutoTable.finalY + 18;
          });
          const pdfBase64 = doc.output("datauristring").split(",")[1];
          const emailResp = await api.post("/export/send-cpa", {
            cpa_email: cpaEmail,
            year: data.year || null,
            pdf_base64: pdfBase64,
            summary: { grand_total_deductible: data.grand_total_deductible, receipt_count: data.receipt_count, line_count: data.line_count },
          });
          if (emailResp.data?.sent) {
            toast.success(`Handoff emailed to ${cpaEmail}`);
          } else if (emailResp.data?.simulated) {
            toast.info("Handoff prepared (email delivery simulated — Brevo key missing)");
          } else {
            toast.warning("Handoff logged but delivery unconfirmed");
          }
        } catch (err) {
          toast.error(err?.response?.data?.detail || "CPA handoff failed");
        } finally {
          setCpaSending(false);
        }
      }
    } catch (e) {
      toast.error(e?.message || e?.response?.data?.detail || "Schedule C export failed");
    } finally {
      setScheduleCLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="fade-up">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-emerald-400 font-mono text-xs uppercase tracking-[0.3em] mb-2">
              Vault
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tighter text-white">
              Receipts
            </h1>
            <p className="text-slate-400 mt-2 text-sm">
              {receipts.length} receipt{receipts.length === 1 ? "" : "s"} · fuzzy full-text search
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportPDF}
              data-testid="export-pdf-btn"
              className="h-11 px-4 rounded-lg glass hover:bg-white/10 text-white font-medium flex items-center gap-2 border-emerald-500/30 transition-colors no-print"
            >
              <FileText size={16} /> Export PDF
            </button>
            <button
              onClick={exportAuditPacket}
              data-testid="export-audit-packet-btn"
              className="h-11 px-4 rounded-lg glass hover:bg-emerald-500/10 text-emerald-200 font-medium flex items-center gap-2 border border-emerald-500/40 transition-colors no-print"
              title="Bundle every Audit-Safe receipt into one IRS-ready PDF with images + statute text"
            >
              <ShieldCheck size={16} /> Audit Packet
            </button>
            <button
              onClick={() => setShowPrintPreview(true)}
              data-testid="print-schedule-c-btn"
              className="h-11 px-4 rounded-lg glass hover:bg-white/10 text-white font-medium flex items-center gap-2 border-emerald-500/30 transition-colors no-print"
              title="Print IRS Schedule C summary"
            >
              <Eye size={16} /> Print Preview
            </button>
            <button
              onClick={exportScheduleC}
              data-testid="export-schedule-c-btn"
              disabled={scheduleCLoading}
              className="h-11 px-4 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold flex items-center gap-2 transition-colors no-print disabled:opacity-70 disabled:cursor-wait"
              title="Download server-side ReportLab Schedule C PDF — IRS Pre-Fill"
            >
              {scheduleCLoading ? (
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
              ) : (
                <FileText size={16} />
              )}
              {scheduleCLoading ? "Generating PDF…" : "Schedule\u00a0C"}
            </button>
            <button
              onClick={exportCSV}
              data-testid="export-csv-btn"
              className="h-11 px-4 rounded-lg glass hover:bg-white/10 text-white font-medium flex items-center gap-2 border-emerald-500/30 transition-colors no-print"
            >
              <Download size={16} /> Export CSV
            </button>
          </div>
        </div>

        {/* CPA Email Handoff — attach Schedule C PDF to Brevo transactional send */}
        <div
          data-testid="cpa-handoff-row"
          className="glass chroma-strip rounded-xl p-3 mb-4 flex flex-col sm:flex-row sm:items-center gap-3 border border-emerald-500/15"
        >
          <label className="flex items-center gap-2 cursor-pointer select-none shrink-0">
            <input
              type="checkbox"
              data-testid="send-to-cpa-checkbox"
              checked={sendToCpa}
              onChange={(e) => setSendToCpa(e.target.checked)}
              className="h-4 w-4 accent-emerald-500"
            />
            <span className="text-sm text-white font-medium">Send to my accountant</span>
            <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-400/80 ml-1">CPA · Brevo</span>
          </label>
          <input
            type="email"
            value={cpaEmail}
            onChange={(e) => setCpaEmail(e.target.value)}
            disabled={!sendToCpa}
            placeholder="cpa@firm.com"
            data-testid="cpa-email-input"
            className="flex-1 h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder:text-slate-500 disabled:opacity-40 focus:outline-none focus:border-emerald-500/50"
          />
          <span
            data-testid="cpa-status-label"
            className={`text-xs font-mono ${cpaSending ? "text-emerald-400 animate-pulse" : "text-slate-500"}`}
          >
            {cpaSending ? "Uploading PDF → Brevo…" : sendToCpa ? "Attach on next Schedule C export" : "Off"}
          </span>
        </div>

        {/* Multi-Year Vault Timeline — filter chips */}
        <div className="mb-4 flex items-center gap-2 overflow-x-auto scroll-touch" data-testid="year-vault-timeline">
          <span className="text-slate-500 font-mono text-[10px] uppercase tracking-widest shrink-0">Vault</span>
          {["all", ...AVAILABLE_YEARS].map((y) => (
            <button
              key={y}
              onClick={() => setYearFilter(y)}
              data-testid={`year-chip-${y}`}
              className={`shrink-0 min-h-[36px] px-3.5 rounded-full text-xs font-mono uppercase tracking-widest transition-colors border ${
                yearFilter === y
                  ? "bg-emerald-500 text-slate-950 border-emerald-400"
                  : "bg-white/5 text-slate-300 border-white/10 hover:bg-white/10"
              }`}
            >
              {y === "all" ? "All Time" : y}
            </button>
          ))}
        </div>

        <div className="glass chroma-strip rounded-2xl p-4 mb-6 flex flex-col sm:flex-row gap-3">
          <div className="flex-1 flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-4 h-12">
            <Search size={18} className="text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder='Try "laptop charger" or "chipotle" or "office"…'
              className="flex-1 bg-transparent outline-none text-white placeholder:text-slate-500"
              data-testid="receipts-search-input"
            />
            {q && (
              <button
                onClick={() => setQ("")}
                className="text-slate-500 hover:text-white text-xs"
              >
                CLEAR
              </button>
            )}
          </div>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger
              className="w-full sm:w-56 h-12 bg-white/5 border-white/10 text-white"
              data-testid="category-filter"
            >
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-white/10 text-white">
              {Object.entries(CAT_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k} className="focus:bg-emerald-500/15 focus:text-emerald-300">
                  {v}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="text-slate-500 text-sm font-mono tracking-widest text-center py-12">
            LOADING RECEIPTS…
          </div>
        ) : filteredReceipts.length === 0 ? (
          <div className="glass chroma-strip rounded-2xl p-16 text-center">
            <ReceiptIcon size={40} className="text-slate-600 mx-auto mb-4" />
            <div className="text-white font-semibold">No matching receipts</div>
            <div className="text-slate-400 text-sm mt-1">
              {yearFilter !== "all"
                ? `Nothing filed for ${yearFilter}. Try another year or clear the filter.`
                : "Try clearing filters or scanning a new receipt."}
            </div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 stagger">
            <style>{`
              .flip-card { perspective: 1400px; }
              .flip-inner {
                position: relative;
                width: 100%;
                height: 100%;
                transition: transform 620ms cubic-bezier(0.4, 0, 0.2, 1);
                transform-style: preserve-3d;
                will-change: transform;
              }
              .flip-card:hover .flip-inner { transform: rotateY(180deg); }
              .flip-face {
                position: absolute; inset: 0;
                -webkit-backface-visibility: hidden;
                backface-visibility: hidden;
                border-radius: 1rem;
              }
              .flip-back { transform: rotateY(180deg); }
            `}</style>
            {filteredReceipts.map((r) => (
              <div
                key={r.id}
                data-testid={`receipt-card-${r.id}`}
                onClick={() => navigate(`/receipts/${r.id}`)}
                className="flip-card cursor-pointer h-56 subpage-hover-row rounded-2xl"
              >
                <div className="flip-inner">
                  {/* FRONT */}
                  <div className={`flip-face glass chroma-strip rounded-2xl p-5 flex flex-col gap-3 card-hover ${r.compliance_verification_token?.badge === "audit-safe" ? "chroma-heartbeat" : ""}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold text-white truncate">{r.merchant}</div>
                        <div className="text-xs text-slate-400 font-mono mt-0.5">{r.date}</div>
                      </div>
                      {r.compliance_verification_token && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setStatuteToken(r.compliance_verification_token); }}
                          className={`shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded border cursor-pointer hover:brightness-125 transition ${
                            r.compliance_verification_token.badge === "audit-safe"
                              ? "bg-emerald-500/15 text-emerald-200 border-emerald-500/40 audit-safe-glow"
                              : r.compliance_verification_token.badge === "partial-deductible"
                              ? "bg-amber-500/15 text-amber-200 border-amber-500/35"
                              : "bg-slate-500/10 text-slate-400 border-slate-500/25"
                          }`}
                          data-testid="compliance-badge"
                          data-variant={r.compliance_verification_token.badge}
                          title={`${r.compliance_verification_token.title} · ${r.compliance_verification_token.deductible_pct}% deductible — click for the full IRS statute`}
                        >
                          <ShieldCheck size={10} />
                          {r.compliance_verification_token.badge === "audit-safe"
                            ? "Audit-Safe"
                            : r.compliance_verification_token.badge === "partial-deductible"
                            ? "50% Meals"
                            : "Personal"}
                        </button>
                      )}
                    </div>

                    <div className="text-3xl font-mono font-bold text-white tracking-tight" data-testid="receipt-total">
                      ${Number(r.total).toFixed(2)}
                    </div>

                    <div className="flex items-center gap-2 flex-wrap mt-auto pt-3 border-t border-white/5">
                      <span className="text-[10px] uppercase tracking-widest font-medium px-2 py-1 rounded bg-white/5 text-slate-300 border border-white/5">
                        {CAT_LABELS[r.category] || r.category}
                      </span>
                      {(r.tags || []).slice(0, 2).map((t) => (
                        <span
                          key={t}
                          className="text-[10px] uppercase tracking-widest font-medium px-2 py-1 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 inline-flex items-center gap-1"
                        >
                          <Tag size={9} /> {t}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* BACK */}
                  <div
                    className="flip-face flip-back rounded-2xl p-5 flex flex-col gap-2 text-slate-100"
                    style={{
                      background: "linear-gradient(150deg, rgba(6,78,59,0.7), rgba(15,23,42,0.85))",
                      border: "1px solid rgba(16,185,129,0.35)",
                      boxShadow: "0 20px 60px -20px rgba(16,185,129,0.5), inset 0 0 40px rgba(16,185,129,0.06)",
                    }}
                  >
                    <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-emerald-300">
                      Quick edit
                    </div>
                    <div className="font-semibold text-white truncate">{r.merchant}</div>
                    <div className="text-xs font-mono text-slate-400 mb-2">{r.date}</div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <div className="text-[9px] uppercase tracking-widest text-slate-500">Subtotal</div>
                        <div className="font-mono text-slate-200">${Number(r.subtotal || 0).toFixed(2)}</div>
                      </div>
                      <div>
                        <div className="text-[9px] uppercase tracking-widest text-slate-500">Tax</div>
                        <div className="font-mono text-slate-200">${Number(r.tax || 0).toFixed(2)}</div>
                      </div>
                    </div>
                    <div className="mt-auto pt-3 border-t border-white/10 flex items-baseline justify-between">
                      <span className="text-[10px] uppercase tracking-widest text-slate-400">Total</span>
                      <span className="font-mono text-xl font-bold text-emerald-400">${Number(r.total).toFixed(2)}</span>
                    </div>
                    <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500">Click to open full detail →</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <PrintPreviewDrawer
        open={showPrintPreview}
        onClose={() => setShowPrintPreview(false)}
        receipts={receipts}
        catLabels={CAT_LABELS}
      />
      <PrintScheduleCSection receipts={receipts} catLabels={CAT_LABELS} />
      <ComplianceStatuteModal
        open={!!statuteToken}
        token={statuteToken}
        onClose={() => setStatuteToken(null)}
      />
    </DashboardLayout>
  );
}
